import React, { useEffect, useState } from 'react';
import { PageShell } from './components/layout/PageShell';
import { AppShell } from './components/layout/AppShell';
import type { PortalSection } from './components/layout/AppShell';
import { Join } from './pages/Join';
import { InviteLanding } from './pages/InviteLanding';
import { ExpiredLink } from './pages/ExpiredLink';
import { Dashboard } from './pages/Dashboard';
import { FlowOverlay } from './components/register/FlowOverlay';
import { ImmersiveFlow } from './components/register/ImmersiveFlow';
import { RegistrationComplete } from './components/register/RegistrationComplete';
import { FLOW_STEPS } from './components/register/flowSteps';
import { EMPTY_DRAFT } from './types/registration';
import type { RegistrationDraft } from './types/registration';
import { CURRENT_MEMBER, memberByName } from './data/community';
import type { Member } from './data/community';
import { useScreenInit } from './useScreenInit.js';
import { PrototypeStatesPanel } from './components/prototype/PrototypeStatesPanel';
import type { PrototypeTarget } from './data/prototypeStates';
type View = 'join' | 'invite' | 'expired' | 'dashboard';
interface AppProps {
  /** Which screen the prototype opens on. */
  view?: 'join' | 'invite' | 'expired' | 'dashboard';
  /** Show the community at twelve members instead of at scale. */
  sparseCommunity?: boolean;
}

/** A finished registration, so the confirmation states can be shown directly. */
const SAMPLE_DRAFT: RegistrationDraft = {
  ...EMPTY_DRAFT,
  firstName: 'Ray',
  lastName: 'Mendel',
  email: 'r.mendel@example.com',
  decades: ['1990s'],
  startYear: '1994',
  endYear: '1999'
};
export function App({
  view = 'join',
  sparseCommunity = false
}: AppProps) {
  const screenInit = useScreenInit();
  useEffect(() => {
    document.title = 'We Are Stepping Stone';
  }, []);
  const [currentView, setCurrentView] = useState<View>(screenInit.view as View ?? view);
  const [section, setSection] = useState<PortalSection>(screenInit.section as PortalSection ?? 'home');
  const [profileOf, setProfileOf] = useState<Member | null>(memberByName(screenInit.profileName as string ?? CURRENT_MEMBER.fullName) ?? null);
  const [open, setOpen] = useState(Boolean(screenInit.open));
  const [stepIndex, setStepIndex] = useState(Number(screenInit.stepIndex ?? 0));
  const [draft, setDraft] = useState<Record<string, unknown>>({
    ...EMPTY_DRAFT
  });
  const [completed, setCompleted] = useState<RegistrationDraft | null>(screenInit.flowDone ? SAMPLE_DRAFT : null);
  const [sparse, setSparse] = useState(screenInit.sparse !== undefined ? Boolean(screenInit.sparse) : sparseCommunity);

  /** Prototype scaffolding: jump straight to a fixture's demonstrated state. */
  function goToPrototypeState(target: PrototypeTarget) {
    setOpen(false);
    setSparse(Boolean(target.sparse));
    if (target.profileName) setProfileOf(memberByName(target.profileName) ?? null);
    if (target.section) setSection(target.section);
    setCurrentView(target.view);
  }
  async function handleSubmit(finalDraft: RegistrationDraft) {
    await new Promise(resolve => setTimeout(resolve, 700));
    setCompleted(finalDraft);
  }
  function reset() {
    setCompleted(null);
    setDraft({
      ...EMPTY_DRAFT
    });
    setStepIndex(0);
    setCurrentView('join');
    setOpen(true);
  }
  return <>
      {currentView === 'dashboard' ? <AppShell section={section} onNavigate={next => {
      if (next === 'profile') setProfileOf(memberByName(CURRENT_MEMBER.fullName) ?? null);
      setSection(next);
    }} onSignOut={() => setCurrentView('join')} data-id="element-0" data-mp="1r3r0nwn6fq6o">
          <Dashboard section={section} sparse={sparse} onNavigate={setSection} profileOf={profileOf} onOpenProfile={member => {
        setProfileOf(member);
        setSection('profile');
      }} data-id="element-1" data-mp="1m5654aivduok" />
        </AppShell> : <PageShell mode="guest" onStart={() => setOpen(true)} startLabel={completed ? 'Your account' : stepIndex > 0 ? 'Continue' : 'Join us'} onSignIn={() => setCurrentView('dashboard')} onSignOut={() => setCurrentView('join')} prototypeLinks={[{
      label: 'Landing page',
      onSelect: () => setCurrentView('join'),
      active: currentView === 'join'
    }, {
      label: 'Invite landing',
      onSelect: () => setCurrentView('invite'),
      active: currentView === 'invite'
    }, {
      label: 'Expired link',
      onSelect: () => setCurrentView('expired'),
      active: currentView === 'expired'
    }, {
      label: 'Memorial profile',
      onSelect: () => {
        setProfileOf(memberByName('Sean Duffy') ?? null);
        setSection('profile');
        setCurrentView('dashboard');
      }
    }]} data-id="element-2" data-mp="qxnifbnxjwqb">
          {currentView === 'invite' ? <InviteLanding onStart={() => setOpen(true)} data-id="element-3" data-mp="206a5du78lt1w" /> : currentView === 'expired' ? <ExpiredLink onStartOver={reset} data-id="element-4" data-mp="2530ge346pygg" /> : <Join onStart={() => setOpen(true)} resumeStep={stepIndex} totalSteps={FLOW_STEPS.length} completed={completed} onStartOver={reset} onEnterPortal={() => setCurrentView('dashboard')} data-id="element-5" data-mp="29wv7vsq17hq3" />}
        </PageShell>}

      <FlowOverlay open={open} onClose={() => setOpen(false)} closeLabel={completed ? 'Close' : 'Close. Your answers are saved'} data-id="element-6" data-mp="2nwe2rjtu0wkg">
        {completed ? <RegistrationComplete draft={completed} onStartOver={reset} onDone={() => {
        setOpen(false);
        setCurrentView('dashboard');
      }} data-id="element-7" data-mp="tj4u1u941f39" /> : <ImmersiveFlow data={draft} onChange={patch => setDraft(current => ({
        ...current,
        ...patch
      }))} stepIndex={stepIndex} onStepChange={setStepIndex} onSubmit={handleSubmit} data-id="element-8" data-mp="1wsh9m0gvhrzk" />}
      </FlowOverlay>

      {open ? null : <PrototypeStatesPanel onGoTo={goToPrototypeState} data-id="element-9" data-mp="2kqg98xcmielw" />}
    </>;
}